
import { ElForm } from 'element-ui/types/form'
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { IAiProvider, IAiProviderType, IUpsertProviderParams, IAiCapability } from '@/types/ai-types'
import { AiService } from '@/services/index'

type DialogMode = 'create' | 'edit'

/**
 * @emit create  新建提交，参数 IUpsertProviderParams
 * @emit update  编辑提交，参数 IUpsertProviderParams
 */
@Component({
    name: 'AiProviderEditDialog'
})
export default class extends Vue {
    @Prop({ required: true }) private providerTypes!: IAiProviderType[]

    private visible = false
    private submitting = false
    private mode: DialogMode = 'create'
    private maskText = ''
    // 编辑时类型不可改（与策略实现强绑定，避免误改导致调用失败）
    private typeDisabled = false

    private form: IUpsertProviderParams = this.emptyForm()

    /** 名称基础校验器：仅校验必填和长度，不再做唯一性校验 */
    private validateName = (rule: any, value: string, callback: Function) => {
        if (!value) {
            callback(new Error('请输入名称'))
            return
        }
        if (value.length > 64) {
            callback(new Error('名称不超过 64 个字符'))
            return
        }
        callback()
    }

    // 模型能力勾选
    private availableCapabilities: IAiCapability[] = []
    private selectedCapabilities: string[] = []
    private loadingCaps: boolean = false

    private rules: any = {
        name: [
            { required: true, validator: this.validateName, trigger: 'blur' }
        ],
        type: [
            { required: true, message: '请选择类型', trigger: 'change' }
        ],
        api_url: [
            { required: true, message: '请输入 API Base URL', trigger: 'blur' }
        ],
        api_key: [
            { required: true, message: '请输入 API Key', trigger: 'blur' }
        ],
        model: [
            { required: true, message: '请输入模型名', trigger: 'blur' }
        ]
    }

    get formRef() {
        return this.$refs.aiForm as ElForm
    }

    @Watch('form.type')
    private onTypeChange(newType: string) {
        if (!newType) {
            this.availableCapabilities = []
            this.selectedCapabilities = []
            return
        }
        this.loadCapabilities(newType)
    }

    private async loadCapabilities(type: string) {
        this.loadingCaps = true
        try {
            const result = await AiService.getAvailableCapabilities(type)
            if (result.status === 200 && result.data) {
                this.availableCapabilities = result.data || []
            } else {
                this.availableCapabilities = []
            }
        } catch {
            this.availableCapabilities = []
        } finally {
            this.loadingCaps = false
        }
    }

    get apiKeyPlaceholder() {
        return this.mode === 'edit'
            ? '留空表示不修改'
            : '请输入明文 API Key，提交后服务端加密落库'
    }

    /* ---------------------- 对外方法 ---------------------- */
    private show(mode: DialogMode, row?: IAiProvider) {
        this.mode = mode
        if (mode === 'edit' && row) {
            this.form = {
                id: row.id,
                name: row.name,
                type: row.type,
                api_url: row.api_url,
                deploy_mode: row.deploy_mode || 'cloud',
                api_key: '',
                model: row.model,
                prompt_template: row.prompt_template || '',
                is_default: row.is_default,
                status: row.status,
                description: row.description || ''
            }
            this.maskText = row.api_key_mask || '—'
            this.typeDisabled = true
            // 编辑时 api_key 非必填（留空不修改）
            this.rules.api_key = []
            // 回填能力勾选
            this.selectedCapabilities = this.parseCapabilitiesFromRow(row)
            if (this.form.type) {
                this.loadCapabilities(this.form.type)
            }
        } else {
            this.form = this.emptyForm()
            this.maskText = ''
            this.typeDisabled = false
            this.rules.api_key = [
                { required: true, message: '请输入 API Key', trigger: 'blur' }
            ]
            this.selectedCapabilities = []
            this.availableCapabilities = []
        }
        this.visible = true
        this.$nextTick(() => {
            this.formRef && this.formRef.clearValidate()
        })
    }

    private hide() {
        this.submitting = false
        this.visible = false
    }

    /* ---------------------- 内部方法 ---------------------- */
    private emptyForm(): IUpsertProviderParams {
        return {
            name: '',
            type: '',
            api_url: '',
            deploy_mode: 'cloud',
            api_key: '',
            model: '',
            prompt_template: '',
            is_default: 0,
            status: 1,
            description: ''
        }
    }

    private onDialogClose(done: Function) {
        if (this.submitting) return
        done()
    }

    /** 从 row 中还原 capabilities 勾选数组 */
    private parseCapabilitiesFromRow(row: any): string[] {
        try {
            if (!row.capabilities) return []
            const caps = typeof row.capabilities === 'string'
                ? JSON.parse(row.capabilities)
                : row.capabilities
            return Object.keys(caps).filter(k => caps[k].supported)
        } catch {
            return []
        }
    }

    private onCancel() {
        if (this.submitting) return
        this.visible = false
    }

    private onSubmit() {
        this.formRef.validate((isValid: boolean) => {
            if (!isValid) return
            this.submitting = true
            const params: IUpsertProviderParams = { ...this.form }
            // 编辑且密钥留空 → 不传 api_key，后端不更新
            if (this.mode === 'edit' && !params.api_key) {
                delete params.api_key
            }
            // 序列化能力勾选 → 完整能力声明 JSON 字符串（含未勾选项 supported:false，供后端覆盖更新）
            const capsObj: Record<string, { supported: boolean }> = {}
            this.availableCapabilities.forEach(cap => {
                capsObj[cap.key] = { supported: false }
            })
            this.selectedCapabilities.forEach(k => {
                capsObj[k] = { supported: true }
            })
            params.capabilities = JSON.stringify(capsObj)
            this.$emit(this.mode === 'edit' ? 'update' : 'create', params)
        })
    }
}
